<template>
  <section class="ranking-card-parts" :class="{ 'ranking-card-parts--is-open': open }">
    <h3 class="ranking-card-parts__title">
      <span class="ranking-card-parts__title-text">{{ rankingCardData.title }}</span>
      <span class="ranking-card-parts__title-number">
        <svg
          width="16"
          height="15"
          viewBox="0 0 16 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.5 1.5465V0H3.5V1.5465H0.5V3.75C0.5 5.64375 1.91675 7.1985 3.743 7.449C4.00458 8.2161 4.46855 8.89828 5.08586 9.42344C5.70317 9.94861 6.45089 10.2972 7.25 10.4325V11.25C7.25 11.6478 7.09196 12.0294 6.81066 12.3107C6.52936 12.592 6.14782 12.75 5.75 12.75H5V14.25H11V12.75H10.25C9.85218 12.75 9.47064 12.592 9.18934 12.3107C8.90804 12.0294 8.75 11.6478 8.75 11.25V10.4325C9.54911 10.2972 10.2968 9.94861 10.9141 9.42344C11.5315 8.89828 11.9954 8.2161 12.257 7.449C14.0833 7.1985 15.5 5.64375 15.5 3.75V1.5465H12.5ZM2 3.75V3.0465H3.5V5.871C3.06162 5.71554 2.68209 5.42822 2.41352 5.04846C2.14495 4.6687 2.0005 4.21513 2 3.75ZM14 3.75C13.9994 4.21521 13.855 4.66885 13.5864 5.04871C13.3179 5.42857 12.9384 5.71606 12.5 5.87175V3.0465H14V3.75Z"
            fill="#ECAA00"
          />
        </svg>
        <span>1</span>
      </span>
    </h3>

    <ranking-card-winner-parts
      v-for="(rankingCardData, index) in getPlayerDataByPosData(0)"
      class="ranking-card-parts__winner"
      :rankingCardData="rankingCardData"
      :key="`rankingWinnerData${index}`"
    />

    <section class="ranking-card-parts__other-list">
      <svg
        class="ranking-card-parts__separate"
        width="313"
        height="33"
        viewBox="0 0 313 33"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          stroke="#fff"
          stroke-miterlimit="10"
          stroke-opacity="0.3"
          d="M313,32.5h0.5c0-8.8-7.2-16-16-16h-282c-8.8,0-16-7.2-16-16l0.5,0"
        />
      </svg>

      <ranking-player-list-item-parts
        v-for="(rankingCardData, index) in getPlayerDataByPosData(1)"
        :key="`RankingPlayer2nd${index}`"
        :rank="rankingCardData.dispRank"
        :playerData="rankingCardData"
      />

      <ranking-player-list-item-parts
        v-for="(rankingCardData, index) in getPlayerDataByPosData(2)"
        :key="`RankingPlayer3rd${index}`"
        :rank="rankingCardData.dispRank"
        :playerData="rankingCardData"
      />
    </section>

    <button class="ranking-card-parts__toggle-button" @click="toggleOpen">
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M16.0996 6.03906L10.2764 11.8623C9.59297 12.5457 8.48493 12.5457 7.80151 11.8623L1.97829 6.03906H4.09961L8.86217 10.8016C8.9598 10.8993 9.11809 10.8993 9.21573 10.8016L13.9783 6.03906H16.0996Z"
          fill="black"
        />
      </svg>
    </button>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from '@vue/composition-api'
import { orderBy, uniqBy, cloneDeep } from 'lodash'
import RankingCardWinnerParts from '@/components/RaceListPage/RaceListPane/parts/RankingCardWinnerParts.vue'
import RankingPlayerListItemParts from '@/components/RaceListPage/RaceListPane/parts/RankingPlayerListItemParts.vue'
import { Multilingual } from '@/store/stores/collectionModule/documents/GeneralTypes'

export type PlayerLinkType = 'thisLap' | 'firstLap' | 'lastLap' | 'poleLap' | 'radio'
export type PlayerLinksData = {
  type: PlayerLinkType
  link: string
}
export type PlayerRankData = {
  number: number
  name: Multilingual
  img: string
  bestData: string
  dispRank: number | null
  bestDataNum: number
  links: PlayerLinksData[]
}

export type RankingCardDataTitle =
  | 'WINNER'
  | 'POLE POSITION'
  | 'TOP SPEED'
  | 'BEST LAP'
  | 'SECTOR 1 BEST TIME'
  | 'SECTOR 2 BEST TIME'
  | 'SECTOR 3 BEST TIME'
  | 'SECTOR 4 BEST TIME'
export type RankingCardData = {
  title: RankingCardDataTitle
  playerData: PlayerRankData[]
}

/**
 * レース一覧画面: レース ランキング カードコンポーネント
 */
export default defineComponent({
  name: 'RaceRankingCardParts',
  components: {
    RankingCardWinnerParts,
    RankingPlayerListItemParts,
  },
  props: {
    rankingCardData: {
      type: Object as PropType<RankingCardData>,
      required: true,
    },
  },
  setup: (props) => {
    const open = ref<boolean>(false)

    /** 1位表示のドライバーの人数 */
    const firstPlayerCount = ref(0)
    /** 2位表示のドライバーの人数 */
    const secondPlayerCount = ref(0)

    const toggleOpen = () => {
      open.value = !open.value
    }

    /**
     * 表示する順位を計算する
     * @param pos 順位
     */
    const calcDispRank = (pos: number) => {
      if (pos === 0) {
        // データ上1番目の順位の表示は1になる
        return 1
      }

      if (pos === 1) {
        // データ上2番目の順位の表示順位データ
        return firstPlayerCount.value + 1
      }

      // データ上3番目の順位の表示順位データ
      return firstPlayerCount.value + secondPlayerCount.value + 1
    }

    /**
     * 指定した順位のドライバーのレコードと、動画再生画面、または、無線再生のためのリンクを取得する。
     * @param pos 順位
     */
    const getPlayerDataByPosData = (pos: number) => {
      // 数値が小さい順にbestDataNumを並べる
      const ranking = orderBy(uniqBy(props.rankingCardData.playerData, 'bestDataNum')).map(
        (playerData) => playerData.bestDataNum,
      )

      const posData: Array<PlayerRankData> = []
      props.rankingCardData.playerData.forEach((player) => {
        const dispPlayerData = cloneDeep(player)

        /**
         * ドライバーのランキングデータのbestDataNumが以下のranking[順位]と一致している場合は、表示対象とする
         * 1位 ranking[0]
         * 2位 ranking[1]
         * 3位 ranking[2]
         */
        if (dispPlayerData.bestDataNum === ranking[pos]) {
          dispPlayerData.dispRank = calcDispRank(pos)
          posData.push(dispPlayerData)
        }
      })

      if (pos === 0) {
        // 1位表示のドライバーの人数
        firstPlayerCount.value = posData.length
      }

      if (pos === 1) {
        // 2位表示のドライバーの人数
        secondPlayerCount.value = posData.length
      }

      return posData
    }

    return {
      open,
      toggleOpen,
      getPlayerDataByPosData,
    }
  },
})
</script>

<style lang="scss" scoped>
.ranking-card-parts {
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  padding: 0;
  background: rgba($black, 0.2);
  border: 1px solid rgba($white, 0.3);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  color: $white;
  overflow: hidden;

  &__title {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 19px 0 16px;
    gap: 10px;
    width: 100%;
    font-weight: 600;
    letter-spacing: 0.04em;

    &-text {
      font-family: $fontHeading;
      font-size: min(#{convertPXtoVW(16), 16px});
      line-height: 1.12;
      text-transform: uppercase;
    }

    &-number {
      display: inline-flex;
      flex-direction: row;
      align-items: center;
      gap: 3px;
      font-size: 20px;
      line-height: 1.2;
    }
  }

  &__other-list {
    position: relative;
    display: flex;
    flex-flow: column nowrap;
    align-items: center;
    justify-items: flex-start;
    gap: 8px;
    width: 100%;
    max-height: 0;
    padding: 0 min(#{convertPXtoVW(15)}, 15px) 0;
    opacity: 0;
    pointer-events: none;
    overflow: hidden;
    transition: all 0.3s ease;
  }

  &__separate {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    transform: translate(0, -50%);

    svg path {
      stroke-width: 1px;
    }
  }

  &__toggle-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: min(#{convertPXtoVW(44)}, 44px);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.6) 100%);
    border-top: 1px solid rgba($black, 0.2);
    cursor: pointer;
    transition: all ease 0.2s;
  }

  &--is-open {
    .ranking-card-parts {
      &__toggle-button {
        background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 100%);
        border-top-color: rgba($black, 0);

        svg {
          transform: rotate(180deg);

          path {
            fill: $white;
          }
        }
      }

      &__separate {
        opacity: 1;
      }

      &__other-list {
        max-height: 100vh;
        padding: min(#{convertPXtoVW(16)}, 16px) min(#{convertPXtoVW(15)}, 15px) 0;
        overflow: visible;
        opacity: 1;
        pointer-events: auto;
      }
    }
  }
}
</style>
